// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { Action } from '../../api/actions/models';
import { BoardDetails, IndicatorSummary, NodeResponse, NodeSummary } from '../../api/boards/models';
import { ReductionGoalResponse } from '../../api/core/models/api/reduction-goal-response';
import { DEFAULT_LANGUAGE } from '../../app.constants';
import { cleanOutcomeValues } from '../../functions/board/clean-outcome-values';
import { getIconPath, getIconPathIdentifier } from '../../functions/get-icon-path';
import { isNotNullOrUndefined } from '../../functions/is-not-null-or-undefined';
import { timelineV1ToTimelineV2 } from '../../functions/model/timeline-v1-to-timeline-v2';
import { toClientActionStatuses } from '../../functions/model/to-client-action-statuses';
import { toClientStakeholders } from '../../functions/model/to-client-stakeholders';
import { conditionallyPrefixImagesWithFilesUrl } from '../../functions/utilities/img-url-utility';
import { BoardReductionGoal } from '../../model/board-reduction-goal';
import { BoardSettings } from '../../model/board-settings';
import { BoardsApiKanbanLane } from '../../model/board-view/kanban-area/kanban-lane';
import { ClientAction } from '../../model/client-model/client-action';
import { ClientBoard } from '../../model/client-model/client-board';
import { ClientIndicator } from '../../model/client-model/client-indicator';
import { ClientNode } from '../../model/client-model/client-node';
import { ExtendedDescriptionType } from '../../model/client-model/extended-description/extended-description-type';
import { ActionProperties } from '../../model/client-model/properties/action.properties';
import { IndicatorImageSource } from '../../model/client-model/properties/indicator-image-source';
import { IndicatorProperties } from '../../model/client-model/properties/indicator.properties';
import { NodeType } from '../../model/client-model/properties/node-type';
import { NodeProperties } from '../../model/client-model/properties/node.properties';
import { RAGRating } from '../../model/client-model/properties/rag-rating';
import { Layout } from '../../model/client-model/settings/board-settings/layout';
import { UnaddressedEmissionsDisplay } from '../../model/client-model/settings/board-settings/unaddressed-emissions-display';
import { Dict } from '../../model/dict';
import { NamedLink } from '../../model/named-link';
import { WeightMetric } from '../../model/prefixed-unit/weight.metric';
import { BoardsApiStakeholder } from '../../model/stakeholder';
import { StrippedDashboardV3 } from '../../standalone/components/self-contained-dashboard-v3/self-contained-dashboard-v3.types';
import { AppConfigService } from '../app-config.service';
import { ApiNode, PublishedBoardV3Data } from '../backend/backend-published-board.types';
import { apiBoardReductionGoalsToBoardReductionGoals } from './helpers';
import { PublishedBoardsModelService } from './published-boards-model.service';

@Injectable({
  providedIn: 'root',
})
export class ModelService {
  constructor(
    private _config: AppConfigService,
    private _publishedBoardsModelService: PublishedBoardsModelService
  ) {}

  toBoardReductionGoal(
    reductionGoalResponse: ReductionGoalResponse | Omit<ReductionGoalResponse, 'createdAt' | 'updatedAt'>
  ): BoardReductionGoal {
    return apiBoardReductionGoalsToBoardReductionGoals([reductionGoalResponse])[0];
  }

  toBoardReductionGoals(reductionGoalResponses: ReductionGoalResponse[]): BoardReductionGoal[] {
    reductionGoalResponses ??= [];
    return apiBoardReductionGoalsToBoardReductionGoals(reductionGoalResponses);
  }

  toClientBoard(board: BoardDetails): ClientBoard {
    const boardSettings: BoardSettings = (board.boardSettings || {}) as BoardSettings;
    if (!boardSettings.transitionTargetNodes) {
      boardSettings.transitionTargetNodes = {
        layout:
          boardSettings?.layout === Layout.GRAPH_AND_BAROMETER ? Layout.GRAPH_AND_BAROMETER : Layout.TARGET_AND_TEXT,
        showCardImages: false,
      };
    }
    if (!boardSettings.internationalisation || !boardSettings.internationalisation.defaultLanguage) {
      boardSettings.internationalisation = {
        defaultLanguage: DEFAULT_LANGUAGE,
      };
    }
    if (!boardSettings.displayUnit) {
      boardSettings.displayUnit = WeightMetric.Tonne;
    }
    if (!boardSettings.unaddressedEmissionsDisplay) {
      boardSettings.unaddressedEmissionsDisplay = UnaddressedEmissionsDisplay.None;
    }

    const timeline = timelineV1ToTimelineV2(boardSettings.timeline);
    boardSettings.timeline = timeline;

    if (!boardSettings.useNewTargetedEmissions) {
      boardSettings.useNewTargetedEmissions = false;
    }

    if (boardSettings.extendedDescription?.data.images) {
      boardSettings.extendedDescription.data.images = boardSettings.extendedDescription.data.images.map((image) => ({
        ...image,
        imgURL: conditionallyPrefixImagesWithFilesUrl(image.imgURL, this._config.baseUrlFiles),
      }));
    }
    if (boardSettings.pointOfEntrySettings?.footer?.images) {
      boardSettings.pointOfEntrySettings.footer.images = boardSettings.pointOfEntrySettings?.footer.images.map(
        (image) => ({
          ...image,
          sourceURL: conditionallyPrefixImagesWithFilesUrl(image.sourceURL, this._config.baseUrlFiles),
        })
      );
    }
    if (boardSettings.pointOfEntrySettings?.header?.backgroundImage?.large) {
      boardSettings.pointOfEntrySettings.header.backgroundImage.large = conditionallyPrefixImagesWithFilesUrl(
        boardSettings.pointOfEntrySettings.header.backgroundImage.large,
        this._config.baseUrlFiles
      );
    }
    if (boardSettings.swimlanes) {
      boardSettings.swimlanes = toClientActionStatuses(<BoardsApiKanbanLane[]>board.boardSettings['swimlanes']);
    }
    if (boardSettings.stakeholders) {
      boardSettings.stakeholders = toClientStakeholders(<BoardsApiStakeholder[]>board.boardSettings['stakeholders']);
    }
    const clientBoard: ClientBoard = {
      _etag: board._etag as string,
      _ts: board._ts as number,
      id: board.id as string,
      boardSettings,
      childNodes: board.childNodes ? (board.childNodes as any) : [],
      supplyChildNodes: board.supplyChildNodes ? (board.supplyChildNodes as any) : [],
      title: board.title || '',
      countryAndCountyIso3166Code: board.countryAndCountyIso3166Code,
      modified: board.modified,
      publishedAt: board.publishedAt,
      isPrimary: board.isPrimary,
    };
    return clientBoard;
  }

  toClientNodes(nodes: NodeSummary[] | Node[]): ClientNode[] {
    return (
      nodes
        .map((node) => this.toClientNode(node))

        // fixing the icons MOD-1265, to be removed after data migration
        .map((node, _, arr) => {
          return typeof node.nodeProperties.icon === 'string'
            ? {
                ...node,
                nodeProperties: {
                  ...node.nodeProperties,
                  icon: getIconPath(getIconPathIdentifier(node, arr)),
                },
              }
            : node;
        })
    );
  }

  toClientNode(node: NodeSummary | NodeResponse | ApiNode): ClientNode {
    let nodeProperties = this.toNodeProperties(node.nodeProperties);

    let _etag: string;
    let _ts: number;
    let boardId: string;
    if (node && '_etag' in node) {
      _etag = node._etag;
      _ts = node._ts;
      boardId = node.boardId;
    }
    if (node && 'eTag' in node) {
      _etag = node.eTag;
    }
    _etag ||= '';
    _ts ||= 0;
    boardId ||= '';

    const clientNode: ClientNode = {
      _etag,
      _ts,
      id: node?.id || '',
      actions: node.actions ? node.actions : [],
      boardId,
      childNodes: node.childNodes ? node.childNodes : [],
      indicators: node.indicators ? node.indicators : [],
      nodeProperties,
      title: node.title || '',
    };

    return clientNode;
  }

  toClientActions(actions: Action[]): ClientAction[] {
    return actions.map((action) => this.toClientAction(action));
  }

  toClientAction(action: Action): ClientAction {
    const actionProperties = this.toActionProperties(action.actionProperties);
    const clientAction: ClientAction = {
      _etag: action._etag as string,
      _ts: action._ts as number,
      id: action.id as string,
      actionProperties,
      auxiliaryProperties: action.auxiliaryProperties,
      boardId: action.boardId as string,
      title: action.title || '',
      legacyCustomElementIds: action.legacyCustomElementIds ?? [],
      transitionElementIds: action.transitionElementIds ?? [],
    };
    return clientAction;
  }

  toClientIndicators(indicators: IndicatorSummary[]): ClientIndicator[] {
    return indicators.map((indicator) => this.toClientIndicator(indicator));
  }

  toClientIndicator(indicator: IndicatorSummary): ClientIndicator {
    const indicatorProperties = this.toIndicatorProperties(indicator?.indicatorProperties || {});
    let _etag: string;
    if (indicator && '_etag' in indicator) {
      _etag = indicator._etag as string;
    }
    if (indicator && 'eTag' in indicator) {
      _etag = indicator.eTag as string;
    }
    _etag ||= '';
    const clientIndicator: ClientIndicator = {
      _etag,
      _ts: ((<any>indicator)?._ts as number) || 0,
      id: (indicator?.id as string) || '',
      boardId: ((<any>indicator)?.boardId as string) || '',
      indicatorProperties,
      title: indicator?.title || '',
    };
    return clientIndicator;
  }

  v3ToStrippedDashboard(envelope: PublishedBoardV3Data): {
    dashboard: StrippedDashboardV3;
    actions: Dict<ClientAction>;
    board: ClientBoard;
  } {
    const entityData = envelope.content.entityData as StrippedDashboardV3 & { actions: Dict<Action> };
    const actions: Dict<ClientAction> = {};
    Object.values(entityData?.actions).forEach((a) => {
      actions[a.id] = this.toClientAction(a);
    });

    const dashboard: StrippedDashboardV3 = {
      colours: entityData.colours,
      endYear: entityData.endYear,
      startYear: entityData.startYear,
      highlightedYear: entityData.highlightedYear,
      labels: entityData.labels,
      leafNodes: entityData.leafNodes,
      nodes: entityData.nodes,
      nodeData: entityData.nodeData,
      totalEmissionsInPlan: entityData.totalEmissionsInPlan,
      unaddressedEmissions: entityData.unaddressedEmissions,
    };

    /**
     * Filter out nonPublic actions
     */
    const filteredActions = Object.values(actions)
      .filter((action) => !action.actionProperties.isWorkingMaterial)
      .reduce((acc, action) => {
        acc[action.id] = action;
        return acc;
      }, {});

    /**
     * If the board object contains the data prop, it's V3.1 so we make sure to map it with the correct method
     */
    if ('data' in envelope.content.boardData.board) {
      const board = this._publishedBoardsModelService.publishedV3_1BoardToClientBoard(
        envelope.content.boardData.board.data
      ) as ClientBoard;
      const isV3_2 =
        'transitionData' in envelope.content.entityData &&
        envelope.content.entityData.transitionData.transitionStoryGroups !== null;
      if (isV3_2) {
        board.childNodes = envelope.content.entityData.transitionData.transitionStoryGroups.map((group) => group.id);
      }
      return { dashboard, actions: filteredActions, board };
    }

    /**
     * If the board object does not contain the data prop, it's V3 so we map it with the default method
     */
    if ('transitionData' in envelope.content.entityData && envelope.content.entityData.transitionData) {
      envelope.content.boardData.board.childNodes =
        envelope.content.entityData.transitionData.transitionStoryGroups.map((group) => group.id);
    }
    return { dashboard, actions: filteredActions, board: this.toClientBoard(envelope.content.boardData.board) };
  }

  private toActionProperties(modelActionProperties: { [key: string]: any } = {}): ActionProperties {
    const defaultProperties: ActionProperties = {
      type: '',
      description: '',
      summary: '',
      accountable: '',
      reference: '',
      editedBy: '',
      isWorkingMaterial: false,
      owningOrganisationId: null,
      createdByOrganisationId: null,
      imgURL: '',
      swimlane: '0',
    };

    const implemented: Date | undefined =
      modelActionProperties.implemented > 0 ? new Date(modelActionProperties.implemented) : undefined;
    const impacted: Date | undefined =
      modelActionProperties.impacted > 0 ? new Date(modelActionProperties.impacted) : undefined;
    const date: Date | undefined = modelActionProperties.date ? new Date(modelActionProperties.date) : undefined;
    let imgURL = modelActionProperties.imgURL;
    if (isNotNullOrUndefined(imgURL)) {
      imgURL = conditionallyPrefixImagesWithFilesUrl(imgURL, this._config.baseUrlFiles);
    }

    let actionProperties: ActionProperties = {
      ...defaultProperties,
      ...modelActionProperties,
      origin: modelActionProperties.actionOriginId,
      implemented,
      impacted,
      date,
      imgURL,
    };

    let readMore: NamedLink[] = actionProperties.readMore;
    if (!readMore) {
      readMore = [];
      if (modelActionProperties.link) {
        readMore.push({ linkUrl: modelActionProperties.link });
      }
      actionProperties = { ...actionProperties, readMore };
    }

    if (actionProperties.costDistribution) {
      actionProperties = {
        ...actionProperties,
        costDistribution: {
          totalCost: actionProperties.costDistribution.totalCost || 0,
          stakeholders: actionProperties.costDistribution.stakeholders || [],
          attributes: actionProperties.costDistribution.attributes || [],
        },
      };
    }

    actionProperties = {
      ...actionProperties,
      ragRating: <RAGRating>actionProperties.ragRating?.toUpperCase(),
    };

    delete (actionProperties as any).link;

    return actionProperties;
  }

  private toIndicatorProperties(modelIndicatorProperties: { [key: string]: any } = {}): IndicatorProperties {
    const defaultProperties: IndicatorProperties = {
      goalDescription: '',
      goalExplanation: '',
      assessmentDescription: '',
      solutionDescription: '',
      diff: 0,
      imgSource: IndicatorImageSource.URL,
    };

    let imgSource = modelIndicatorProperties.imgSource || IndicatorImageSource.URL;
    let imgURL = '';
    let imgData = '';
    if (imgSource === IndicatorImageSource.URL) {
      imgURL = modelIndicatorProperties.imgURL || '';
    } else {
      imgData = modelIndicatorProperties.imgData || '';
    }
    const unknownDiff = modelIndicatorProperties.unknownDiff || modelIndicatorProperties.diff === undefined;
    let indicatorProperties: IndicatorProperties = {
      ...defaultProperties,
      ...modelIndicatorProperties,
      imgURL,
      imgData,
      unknownDiff,
    };
    return indicatorProperties;
  }

  private toNodeProperties(modelNodeProperties: { [key: string]: any } = {}): NodeProperties {
    const defaultProperties: NodeProperties = {
      icon: '',
      type: NodeType.Emission,
      imgURL: '',
      thumbnailURL: '',
      description: '',
      co2e: 0,
      diff: 0,
      data: [],
      diagramHeader: '',
    };
    let extendedDescription = modelNodeProperties.extendedDescription;
    if (!extendedDescription) {
      extendedDescription = {
        type: ExtendedDescriptionType.ImagesAndTexts,
        data: {
          images: [],
          texts: [
            {
              title: '',
              body: '',
            },
          ],
        },
      };
    }
    extendedDescription.data.images = extendedDescription.data.images.map((image) => ({
      ...image,
      imgURL: conditionallyPrefixImagesWithFilesUrl(image.imgURL, this._config.baseUrlFiles),
    }));
    modelNodeProperties.extendedDescription = extendedDescription;

    const nodeProperties: NodeProperties = { ...defaultProperties, ...modelNodeProperties };
    if (nodeProperties.transitionTargetData) {
      const outcome = nodeProperties.transitionTargetData.transitionOutcome?.outcome ?? {};

      nodeProperties.transitionTargetData.transitionOutcome = { outcome: cleanOutcomeValues(outcome) };
    }
    return nodeProperties;
  }
}
